<template>
  <div v-if="isCN">
    <section class="py-12">
      <div class="container-wrap">
        <h2 class="text-h2">{{ t('title') }}</h2>
      </div>
    </section>
    <SuiteTopicsStage :suite-data="stageData01" />
    <SuiteListProduct :suite-data="productData01" />
    <SuiteTopicsStage :suite-data="stageData02" />
    <SuiteListProduct :suite-data="productData02" />
    <SuiteTopicsStage :suite-data="stageData03" />
    <SuiteListProduct :suite-data="productData03" />
  </div>

  <section
    v-else
    class="bg-gradient-to-b from-blue-400/10 to-[#39C3FF]/10 py-12"
  >
    <div class="container-wrap">
      <h2
        class="text-h2 mb-8 bg-gradient-to-tr from-blue-400 to-[#39C3FF] bg-clip-text text-transparent tablet:mb-12"
      >
        {{ t('title') }}
      </h2>
      <div
        v-for="stage in stages"
        :key="stage.label"
        class="py-10 last:pb-0 tablet:py-12 desktop:grid desktop:grid-cols-3 desktop:gap-6"
      >
        <div class="mb-6 desktop:col-span-1 desktop:mb-0">
          <p
            class="mb-2 bg-gradient-to-tr from-blue-400 to-[#39C3FF] bg-clip-text text-xs font-bold text-transparent"
          >
            {{ stage.label }}
          </p>
          <h3
            class="mb-2 text-tablet-h3 font-bold tablet:text-mobile-h2 desktop:text-tablet-h2"
          >
            {{ stage.title }}
          </h3>
          <p class="text-sm text-gray-600 tablet:text-base">
            {{ stage.description }}
          </p>
        </div>
        <div
          class="grid gap-4 tablet:gap-6 desktop:col-span-2"
          :class="
            stage.items.length === 1
              ? 'tablet:grid-cols-2'
              : 'tablet:grid-cols-2'
          "
        >
          <AreaTopicsDoeDigitalJourneyCard
            v-for="item in stage.items"
            :key="item.name"
            :name="item.name"
            :description="item.description"
            :thumbnail="item.thumbnail"
            :url="item.link"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { IListProduct } from '@/components/Shared/Suite/ListProduct/index.vue';
  import type { IStage } from '@/components/Shared/Suite/TopicsStage/index.vue';

  interface IStageItem {
    name: string;
    description: string;
    link: string;
    thumbnail: string;
  }

  interface IStageBlock {
    label: string;
    title: string;
    description: string;
    items: IStageItem[];
  }

  const { t, locale } = useI18n({ useScope: 'local' });
  const { isCN } = useLanguage();
  const { blobDomain } = useWebsite();
  const { provideResponsiveImgName } = useImagePath();

  const stages = computed((): IStageBlock[] => {
    return [
      {
        label: t('stage-1-label'),
        title: t('stage-1-title'),
        description: t('stage-1-description'),
        items: [
          {
            name: t('stage-1-item-1-title'),
            description: t('stage-1-item-1-description'),
            link: `/${locale.value}/marketplace/products/advantech.edgehub`,
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('topic-edgehub-img')}`,
          },
        ],
      },
      {
        label: t('stage-2-label'),
        title: t('stage-2-title'),
        description: t('stage-2-description'),
        items: [
          ...(!['en-jp', 'ja-jp'].includes(locale.value)
            ? [
                {
                  name: t('stage-2-item-1-title'),
                  description: t('stage-2-item-1-description'),
                  link: `/${locale.value}/marketplace/solutions/wise-iotsuite`,
                  thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('topic-iotsuite-img')}`,
                },
              ]
            : []),
          {
            name: t('stage-2-item-2-title'),
            description: t('stage-2-item-2-description'),
            link: `/${locale.value}/marketplace/products/advantech.iems-ecowatch`,
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('topic-ecowatch-img')}`,
          },
        ],
      },
      {
        label: t('stage-3-label'),
        title: t('stage-3-title'),
        description: t('stage-3-description'),
        items: [
          ...(!['en-americas', 'en-jp', 'ja-jp'].includes(locale.value)
            ? [
                {
                  name: t('stage-3-item-1-title'),
                  description: t('stage-3-item-1-description'),
                  link: `/${locale.value}/marketplace/products/advantech.phm`,
                  thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('topic-phm-img')}`,
                },
              ]
            : []),
          {
            name: t('stage-3-item-2-title'),
            description: t('stage-3-item-2-description'),
            link: `/${locale.value}/marketplace/help/sales-support?IssueType=1`,
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('topic-iems-ai-agent-img')}`,
          },
        ],
      },
    ];
  });

  const stageData01 = computed((): IStage => {
    return {
      component: 'topics-stage',
      meta: {
        navigable: '',
      },
      data: {
        sectionName: t('stage-1-label'),
        title: t('stage-1-title'),
        description: t('stage-1-description'),
      },
    };
  });

  const productData01 = computed((): IListProduct => {
    return {
      component: 'list-product',
      meta: {
        navigable: '',
      },
      data: {
        title: t('stage-1-subtitle'),
        items: [
          {
            name: t('stage-1-item-1-title'),
            description: t('stage-1-item-1-description'),
            link: `/${locale.value}/marketplace/products/advantech.edgehub`,
            iconThumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/topic-edgehub-icon.webp`,
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('topic-edgehub-img')}`,
          },
        ],
      },
    };
  });

  const stageData02 = computed((): IStage => {
    return {
      component: 'topics-stage',
      meta: {
        navigable: '',
      },
      data: {
        sectionName: t('stage-2-label'),
        title: t('stage-2-title'),
        description: t('stage-2-description'),
      },
    };
  });

  const productData02 = computed((): IListProduct => {
    return {
      component: 'list-product',
      meta: {
        navigable: '',
      },
      data: {
        title: t('stage-2-subtitle'),
        items: [
          {
            name: t('stage-2-item-1-title'),
            description: t('stage-2-item-1-description'),
            link: `/${locale.value}/marketplace/solutions/wise-iotsuite`,
            iconThumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/topic-iotsuite-icon.webp`,
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('topic-iotsuite-img')}`,
          },
          {
            name: t('stage-2-item-2-title'),
            description: t('stage-2-item-2-description'),
            link: `/${locale.value}/marketplace/products/advantech.iems-ecowatch`,
            iconThumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/topic-ecowatch-icon.webp`,
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('topic-ecowatch-img')}`,
          },
        ],
      },
    };
  });

  const stageData03 = computed((): IStage => {
    return {
      component: 'topics-stage',
      meta: {
        navigable: '',
      },
      data: {
        sectionName: t('stage-3-label'),
        title: t('stage-3-title'),
        description: t('stage-3-description'),
      },
    };
  });

  const productData03 = computed((): IListProduct => {
    return {
      component: 'list-product',
      meta: {
        navigable: '',
      },
      data: {
        title: t('stage-3-subtitle'),
        items: [
          {
            name: t('stage-3-item-1-title'),
            description: t('stage-3-item-1-description'),
            link: `/${locale.value}/marketplace/products/advantech.phm`,
            iconThumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/topic-phm-icon.webp`,
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('topic-phm-img')}`,
          },
          {
            name: t('stage-3-item-2-title'),
            description: t('stage-3-item-2-description'),
            link: `/${locale.value}/marketplace/help/sales-support?IssueType=1`,
            iconThumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/topic-iems-ai-agent-icon.webp`,
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('topic-iems-ai-agent-img')}`,
          },
        ],
      },
    };
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "Digital O&M Journey",
    "stage-1-label": "Stage 1",
    "stage-1-title": "Edge Connectivity & Device Management",
    "stage-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "stage-1-subtitle": "Unified Edge Operations & Secure Access",
    "stage-1-item-1-title": "EdgeHub",
    "stage-1-item-1-description": "Securely connect edge devices and industrial equipment with plug-and-play onboarding, scalable device management, and encrypted tunnels for reliable remote access.",
    "stage-2-label": "Stage 2",
    "stage-2-title": "Remote Multi-site Operations",
    "stage-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "stage-2-subtitle": "IoT Data Platform & ISO 50001 Energy Management",
    "stage-2-item-1-title": "IoTSuite",
    "stage-2-item-1-description": "Unify and analyze heterogeneous operational data, enable real-time monitoring across systems, and build industrial applications with an open low-code platform.",
    "stage-2-item-2-title": "ECOWatch",
    "stage-2-item-2-description": "Track and benchmark energy consumption, detect anomalies, and generate actionable insights to help enterprises improve energy efficiency and sustainability.",
    "stage-3-label": "Stage 3",
    "stage-3-title": "AI Optimization",
    "stage-3-description": "Advance from monitoring to optimization—enabling predictive insights, intelligent forecasting, and AI-driven decision support for reliability and sustainability.",
    "stage-3-subtitle": "AI-Driven Asset Health & Energy Optimization",
    "stage-3-item-1-title": "PHM",
    "stage-3-item-1-description": "Apply AI to monitor asset health, detect anomalies early, estimate remaining useful life, and identify failure patterns across critical industrial equipment.",
    "stage-3-item-2-title": "iEMS.AI Agent",
    "stage-3-item-2-description": "An AI agent that forecasts energy demand, analyzes consumption patterns, and provides recommendations for cost optimization, peak shaving, and load shifting."
  },
  "en-jp": {
    "title": "Digital O&M Journey",
    "stage-1-label": "Stage 1",
    "stage-1-title": "Edge Connectivity & Device Management",
    "stage-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "stage-1-subtitle": "Unified Edge Operations & Secure Access",
    "stage-1-item-1-title": "EdgeHub",
    "stage-1-item-1-description": "Securely connect edge devices and industrial equipment with plug-and-play onboarding, scalable device management, and encrypted tunnels for reliable remote access.",
    "stage-2-label": "Stage 2",
    "stage-2-title": "Remote Multi-site Operations",
    "stage-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "stage-2-subtitle": "IoT Data Platform & ISO 50001 Energy Management",
    "stage-2-item-2-title": "ECOWatch",
    "stage-2-item-2-description": "Track and benchmark energy consumption, detect anomalies, and generate actionable insights to help enterprises improve energy efficiency and sustainability.",
    "stage-3-label": "Stage 3",
    "stage-3-title": "AI Optimization",
    "stage-3-description": "Advance from monitoring to optimization—enabling predictive insights, intelligent forecasting, and AI-driven decision support for reliability and sustainability.",
    "stage-3-subtitle": "AI-Driven Asset Health & Energy Optimization",
    "stage-3-item-2-title": "iEMS.AI Agent",
    "stage-3-item-2-description": "An AI agent that forecasts energy demand, analyzes consumption patterns, and provides recommendations for cost optimization, peak shaving, and load shifting."
  },
  "en-tw": {
    "title": "Digital O&M Journey",
    "stage-1-label": "Stage 1",
    "stage-1-title": "Edge Connectivity & Device Management",
    "stage-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "stage-1-subtitle": "Unified Edge Operations & Secure Access",
    "stage-1-item-1-title": "EdgeHub",
    "stage-1-item-1-description": "Securely connect edge devices and industrial equipment with plug-and-play onboarding, scalable device management, and encrypted tunnels for reliable remote access.",
    "stage-2-label": "Stage 2",
    "stage-2-title": "Remote Multi-site Operations",
    "stage-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "stage-2-subtitle": "IoT Data Platform & ISO 50001 Energy Management",
    "stage-2-item-1-title": "IoTSuite",
    "stage-2-item-1-description": "Unify and analyze heterogeneous operational data, enable real-time monitoring across systems, and build industrial applications with an open low-code platform.",
    "stage-2-item-2-title": "ECOWatch",
    "stage-2-item-2-description": "Track and benchmark energy consumption, detect anomalies, and generate actionable insights to help enterprises improve energy efficiency and sustainability.",
    "stage-3-label": "Stage 3",
    "stage-3-title": "AI Optimization",
    "stage-3-description": "Advance from monitoring to optimization—enabling predictive insights, intelligent forecasting, and AI-driven decision support for reliability and sustainability.",
    "stage-3-subtitle": "AI-Driven Asset Health & Energy Optimization",
    "stage-3-item-1-title": "PHM",
    "stage-3-item-1-description": "Apply AI to monitor asset health, detect anomalies early, estimate remaining useful life, and identify failure patterns across critical industrial equipment.",
    "stage-3-item-2-title": "iEMS.AI Agent",
    "stage-3-item-2-description": "An AI agent that forecasts energy demand, analyzes consumption patterns, and provides recommendations for cost optimization, peak shaving, and load shifting."
  },
  "en-kr": {
    "title": "Digital O&M Journey",
    "stage-1-label": "Stage 1",
    "stage-1-title": "Edge Connectivity & Device Management",
    "stage-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "stage-1-subtitle": "Unified Edge Operations & Secure Access",
    "stage-1-item-1-title": "EdgeHub",
    "stage-1-item-1-description": "Securely connect edge devices and industrial equipment with plug-and-play onboarding, scalable device management, and encrypted tunnels for reliable remote access.",
    "stage-2-label": "Stage 2",
    "stage-2-title": "Remote Multi-site Operations",
    "stage-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "stage-2-subtitle": "IoT Data Platform & ISO 50001 Energy Management",
    "stage-2-item-1-title": "IoTSuite",
    "stage-2-item-1-description": "Unify and analyze heterogeneous operational data, enable real-time monitoring across systems, and build industrial applications with an open low-code platform.",
    "stage-2-item-2-title": "ECOWatch",
    "stage-2-item-2-description": "Track and benchmark energy consumption, detect anomalies, and generate actionable insights to help enterprises improve energy efficiency and sustainability.",
    "stage-3-label": "Stage 3",
    "stage-3-title": "AI Optimization",
    "stage-3-description": "Advance from monitoring to optimization—enabling predictive insights, intelligent forecasting, and AI-driven decision support for reliability and sustainability.",
    "stage-3-subtitle": "AI-Driven Asset Health & Energy Optimization",
    "stage-3-item-1-title": "PHM",
    "stage-3-item-1-description": "Apply AI to monitor asset health, detect anomalies early, estimate remaining useful life, and identify failure patterns across critical industrial equipment.",
    "stage-3-item-2-title": "iEMS.AI Agent",
    "stage-3-item-2-description": "An AI agent that forecasts energy demand, analyzes consumption patterns, and provides recommendations for cost optimization, peak shaving, and load shifting."
  },
  "en-americas": {
    "title": "Digital O&M Journey",
    "stage-1-label": "Stage 1",
    "stage-1-title": "Edge Connectivity & Device Management",
    "stage-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "stage-1-subtitle": "Unified Edge Operations & Secure Access",
    "stage-1-item-1-title": "EdgeHub",
    "stage-1-item-1-description": "Securely connect edge devices and industrial equipment with plug-and-play onboarding, scalable device management, and encrypted tunnels for reliable remote access.",
    "stage-2-label": "Stage 2",
    "stage-2-title": "Remote Multi-site Operations",
    "stage-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "stage-2-subtitle": "IoT Data Platform & ISO 50001 Energy Management",
    "stage-2-item-1-title": "IoTSuite",
    "stage-2-item-1-description": "Unify and analyze heterogeneous operational data, enable real-time monitoring across systems, and build industrial applications with an open low-code platform.",
    "stage-2-item-2-title": "ECOWatch",
    "stage-2-item-2-description": "Track and benchmark energy consumption, detect anomalies, and generate actionable insights to help enterprises improve energy efficiency and sustainability.",
    "stage-3-label": "Stage 3",
    "stage-3-title": "AI Optimization",
    "stage-3-description": "Advance from monitoring to optimization—enabling predictive insights, intelligent forecasting, and AI-driven decision support for reliability and sustainability.",
    "stage-3-subtitle": "AI-Driven Asset Health & Energy Optimization",
    "stage-3-item-2-title": "iEMS.AI Agent",
    "stage-3-item-2-description": "An AI agent that forecasts energy demand, analyzes consumption patterns, and provides recommendations for cost optimization, peak shaving, and load shifting."
  },
  "en-asia-pacific": {
    "title": "Digital O&M Journey",
    "stage-1-label": "Stage 1",
    "stage-1-title": "Edge Connectivity & Device Management",
    "stage-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "stage-1-subtitle": "Unified Edge Operations & Secure Access",
    "stage-1-item-1-title": "EdgeHub",
    "stage-1-item-1-description": "Securely connect edge devices and industrial equipment with plug-and-play onboarding, scalable device management, and encrypted tunnels for reliable remote access.",
    "stage-2-label": "Stage 2",
    "stage-2-title": "Remote Multi-site Operations",
    "stage-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "stage-2-subtitle": "IoT Data Platform & ISO 50001 Energy Management",
    "stage-2-item-1-title": "IoTSuite",
    "stage-2-item-1-description": "Unify and analyze heterogeneous operational data, enable real-time monitoring across systems, and build industrial applications with an open low-code platform.",
    "stage-2-item-2-title": "ECOWatch",
    "stage-2-item-2-description": "Track and benchmark energy consumption, detect anomalies, and generate actionable insights to help enterprises improve energy efficiency and sustainability.",
    "stage-3-label": "Stage 3",
    "stage-3-title": "AI Optimization",
    "stage-3-description": "Advance from monitoring to optimization—enabling predictive insights, intelligent forecasting, and AI-driven decision support for reliability and sustainability.",
    "stage-3-subtitle": "AI-Driven Asset Health & Energy Optimization",
    "stage-3-item-1-title": "PHM",
    "stage-3-item-1-description": "Apply AI to monitor asset health, detect anomalies early, estimate remaining useful life, and identify failure patterns across critical industrial equipment.",
    "stage-3-item-2-title": "iEMS.AI Agent",
    "stage-3-item-2-description": "An AI agent that forecasts energy demand, analyzes consumption patterns, and provides recommendations for cost optimization, peak shaving, and load shifting."
  },
  "en-emea": {
    "title": "Digital O&M Journey",
    "stage-1-label": "Stage 1",
    "stage-1-title": "Edge Connectivity & Device Management",
    "stage-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "stage-1-subtitle": "Unified Edge Operations & Secure Access",
    "stage-1-item-1-title": "EdgeHub",
    "stage-1-item-1-description": "Securely connect edge devices and industrial equipment with plug-and-play onboarding, scalable device management, and encrypted tunnels for reliable remote access.",
    "stage-2-label": "Stage 2",
    "stage-2-title": "Remote Multi-site Operations",
    "stage-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "stage-2-subtitle": "IoT Data Platform & ISO 50001 Energy Management",
    "stage-2-item-1-title": "IoTSuite",
    "stage-2-item-1-description": "Unify and analyze heterogeneous operational data, enable real-time monitoring across systems, and build industrial applications with an open low-code platform.",
    "stage-2-item-2-title": "ECOWatch",
    "stage-2-item-2-description": "Track and benchmark energy consumption, detect anomalies, and generate actionable insights to help enterprises improve energy efficiency and sustainability.",
    "stage-3-label": "Stage 3",
    "stage-3-title": "AI Optimization",
    "stage-3-description": "Advance from monitoring to optimization—enabling predictive insights, intelligent forecasting, and AI-driven decision support for reliability and sustainability.",
    "stage-3-subtitle": "AI-Driven Asset Health & Energy Optimization",
    "stage-3-item-1-title": "PHM",
    "stage-3-item-1-description": "Apply AI to monitor asset health, detect anomalies early, estimate remaining useful life, and identify failure patterns across critical industrial equipment.",
    "stage-3-item-2-title": "iEMS.AI Agent",
    "stage-3-item-2-description": "An AI agent that forecasts energy demand, analyzes consumption patterns, and provides recommendations for cost optimization, peak shaving, and load shifting."
  },
  "ja-jp": {
    "title": "デジタルO&Mの導入ステージ",
    "stage-1-label": "ステージ1",
    "stage-1-title": "エッジ接続とデバイス管理",
    "stage-1-description": "組み込み型の遠隔管理機能と継続的なデバイス可視化により、安全で常時稼働する運用環境を実現します",
    "stage-1-subtitle": "統合エッジ運用とセキュアアクセス",
    "stage-1-item-1-title": "EdgeHub",
    "stage-1-item-1-description": "プラグアンドプレイ導入とスケーラブルなデバイス管理により、暗号化通信を通じて安全なリモートアクセスを実現します。",
    "stage-2-label": "ステージ2",
    "stage-2-title": "遠隔マルチサイト運用",
    "stage-2-description": "各拠点の可視性を集約し、運用の標準化を推進するとともに、エネルギー性能のベンチマークを実現します。",
    "stage-2-subtitle": "IoTデータプラットフォームとISO 50001エネルギー管理",
    "stage-2-item-2-title": "ECOWatch",
    "stage-2-item-2-description": "エネルギー使用状況を可視化し異常を検知、実用的なインサイトにより効率と持続可能性の向上を支援します。",
    "stage-3-label": "ステージ3",
    "stage-3-title": "AI最適化",
    "stage-3-description": "モニタリングから最適化へ進化し、予測分析とAIによる意思決定支援により、運用効率を向上させます。",
    "stage-3-subtitle": "AIによる設備健全性管理とエネルギー最適化",
    "stage-3-item-2-title": "iEMS.AI Agent",
    "stage-3-item-2-description": "エネルギー需要を予測し、使用パターンを分析。コスト最適化や負荷制御に向けた最適な意思決定を支援します。"
  },
  "zh-cn": {
    "title": "Digital O&M Journey",
    "stage-1-label": "",
    "stage-1-title": "Stage 1: Edge Connectivity & Device Management",
    "stage-1-description": "Deliver secure, always-on operations powered by built-in remote management and continuous device insight.",
    "stage-1-subtitle": "Unified Edge Operations & Secure Access",
    "stage-1-item-1-title": "EdgeHub",
    "stage-1-item-1-description": "Securely connect edge devices and industrial equipment with plug-and-play onboarding, scalable device management, and encrypted tunnels for reliable remote access.",
    "stage-2-label": "",
    "stage-2-title": "Stage 2: Remote Multi-site Operations",
    "stage-2-description": "Centralize visibility, standardize operations, and benchmark energy performance across all sites.",
    "stage-2-subtitle": "IoT Data Platform & ISO 50001 Energy Management",
    "stage-2-item-1-title": "IoTSuite",
    "stage-2-item-1-description": "Unify and analyze heterogeneous operational data, enable real-time monitoring across systems, and build industrial applications with an open low-code platform.",
    "stage-2-item-2-title": "ECOWatch",
    "stage-2-item-2-description": "Track and benchmark energy consumption, detect anomalies, and generate actionable insights to help enterprises improve energy efficiency and sustainability.",
    "stage-3-label": "",
    "stage-3-title": "Stage 3: AI Optimization",
    "stage-3-description": "Advance from monitoring to optimization—enabling predictive insights, intelligent forecasting, and AI-driven decision support for reliability and sustainability.",
    "stage-3-subtitle": "AI-Driven Asset Health & Energy Optimization",
    "stage-3-item-1-title": "PHM",
    "stage-3-item-1-description": "Apply AI to monitor asset health, detect anomalies early, estimate remaining useful life, and identify failure patterns across critical industrial equipment.",
    "stage-3-item-2-title": "iEMS.AI Agent",
    "stage-3-item-2-description": "An AI agent that forecasts energy demand, analyzes consumption patterns, and provides recommendations for cost optimization, peak shaving, and load shifting."
  },
  "zh-tw": {
    "title": "數位維運旅程",
    "stage-1-label": "階段一",
    "stage-1-title": "邊緣連線與設備管理",
    "stage-1-description": "透過內建遠端管理與持續設備監控，確保安全且穩定的營運",
    "stage-1-subtitle": "統一邊緣維運與安全存取",
    "stage-1-item-1-title": "EdgeHub",
    "stage-1-item-1-description": "透過即插即用的快速部署、可擴展的設備管理，以及加密通道技術，安全連接邊緣裝置與工業設備，確保可靠的遠端存取能力",
    "stage-2-label": "階段二",
    "stage-2-title": "遠端多據點維運管理",
    "stage-2-description": "集中管理各據點的可視性、標準化維運流程，並對能源績效進行系統化的基準評比",
    "stage-2-subtitle": "IoT 數據平台與 ISO 50001 能源管理",
    "stage-2-item-1-title": "IoTSuite",
    "stage-2-item-1-description": "整合並分析異質化的營運數據，實現跨系統的即時監控，並透過低代碼平台快速建構工業應用程式",
    "stage-2-item-2-title": "ECOWatch",
    "stage-2-item-2-description": "追蹤能源使用、識別異常，更提供可行動的洞察建議，協助企業提升能源效率與永續表現",
    "stage-3-label": "階段三",
    "stage-3-title": "AI 智慧優化",
    "stage-3-description": "AI 驅動設備健康管理與能源優化",
    "stage-3-subtitle": "AI 驅動設備健康管理與能源優化",
    "stage-3-item-1-title": "PHM",
    "stage-3-item-1-description": "運用 AI 監測設備健康狀態，提前偵測異常並預測剩餘壽命，識別關鍵設備的故障模式，強化預測維護能力",
    "stage-3-item-2-title": "iEMS.AI Agent",
    "stage-3-item-2-description": "分析能源使用模式並預測需求，提供成本優化與負載調整建議"
  }
}
</i18n>
